/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui hooks
import { useMediaQuery, Box } from "@mui/material";

// MSAL imports
import { MsalProvider } from "@azure/msal-react";
import msalInstance from "authConfig";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React examples
import Sidenav from "examples/Sidenav";
import CustomSidenav from "examples/Sidenav/CustomSidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 3 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 3 PRO React routes
import routes from "routes";
import AuthCallback from "layouts/authentication/callback";

// Material Dashboard 3 PRO React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import brandWhite from "assets/images/doorsharp-logo.png";
import brandDark from "assets/images/doorsharp-logo.png";
import './app.css'
import Header from "../src/components/Header/Header"
import AppHeader from "../src/components/Header/AppHeader";
import { FilterProvider } from "../src/context/FilterContext";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const isPhone = useMediaQuery('(max-width:599px)'); // Phones only - sidenav overlays

  // Cache for the rtl
  useEffect(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        //  console.log(route.component, "$$$$$$^^^&&&")
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const headerConfig = [
    {
      match: "/dashboards/my-dashboard",
      title: "My Dashboard",
      isFilterIcon: true,
      isFilter: true,
    },
    {
      match: "/dashboards/financials",
      title: "Financials",
      isFilterIcon: true,
      isFilter: true
    },
    {
      match: "/dashboards/commercial",
      title: "Commercial",
      isFilterIcon: true,
      isFilter: true
    },
    {
      match: "/dashboards/operations",
      title: " Operations",
      isFilterIcon: true,
      isFilter: true
    },
    {
      match: "/dashboards/technology",
      title: " Technology",
      isFilterIcon: false,
      isFilter: false
    },
    {
      match: "/dashboards/marketing",
      title: "Marketing",
      isFilterIcon: true,
      isFilter: true
    },
    {
      match: "/dashboards/human-resources",
      title: " Human Resources",
      isFilterIcon: false,
      isFilter: false
    },
    {
      match: "/network/retailers",
      title: "Retailers",
      isFilterIcon: false,
      isFilter: false
    },
    {
      match: "/network/distributors",
      title: "Distributors",
      isFilterIcon: false,
      isFilter: false
    },
    {
      match: "/insights/commercial",
      title: "Commercial Insights",
      isFilterIcon: true,
      isFilter: true
    },
    {
      match: "/intelligence",
      title: "Intelligence",
      isFilterIcon: false,
      isFilter: false
    },


  ]
  const HIDE_HEADER_ROUTES = [
    "/network/distributors/details",
    "/network/retailers/details",
  ];

  const currentHeader = headerConfig.find(
    (cfg) =>
      pathname.startsWith(cfg.match) &&
      !HIDE_HEADER_ROUTES.some((r) => pathname.startsWith(r))
  );


  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const isAuthRoute = pathname.startsWith("/authentication") || pathname.startsWith("/auth/callback");
  const isintelligencePage = pathname.startsWith("/intelligence")
  const [isHovercheckExpanded, setIsHovercheckExpanded] = useState(false);
  console.log(isHovercheckExpanded, "checkinggggg")
  return (
    <FilterProvider>
    <>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <CssBaseline />

            {layout === "dashboard" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={
                    (transparentSidenav && !darkMode) || whiteSidenav
                      ? brandDark
                      : brandWhite
                  }
                  brandName="DoorSharp Analytics"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                  setIsHovercheckExpanded={setIsHovercheckExpanded}
                />
                <Configurator />
              </>
            )}

            <Routes>
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/dashboards/my-dashboard" replace />} />
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />

          {layout === "dashboard" && !isAuthRoute && (
            <>
              <CustomSidenav
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                setIsHovercheckExpanded={setIsHovercheckExpanded}
              />
              <Configurator />
              <MDBox
                className='mbl-header-layout'
                sx={{
                  display: {
                    xs: "block",
                    sm: "block",
                    md: "none",
                  },
                  marginBottom: { xs: "56px", sm: "56px", md: "0" },
                }}

              >
                <AppHeader />
              </MDBox>
              {currentHeader && (
                <Box sx={{
                  ml:
                    // 1️⃣ Auth routes → no margin
                    isAuthRoute
                      ? "0px"
                      : // 2️⃣ Non-auth routes
                      isPhone
                        ? "0px" // Phone: sidenav overlays content
                        : miniSidenav && !isHovercheckExpanded
                          ? "68px" // Tablet/Desktop collapsed
                          : "260px", // Tablet/Desktop expanded
                  transition: "margin-left 0.25s ease",
                  background: '#FFFFFF !important'
                }}>
                  <Header
                    title={currentHeader.title}
                    isfiltericon={currentHeader.isFilterIcon}
                    isfilter={currentHeader.isFilter}
                  />
                </Box>

              )}

            </>

          )}

          {console.log(layout, "*******layout")}
          <MDBox
            sx={{
              position: "relative",
              ml:
                // 1️⃣ Auth routes → no margin
                isAuthRoute
                  ? "0px"
                  : // 2️⃣ Non-auth routes
                  isPhone
                    ? "0px" // Phone: sidenav overlays content
                    : miniSidenav && !isHovercheckExpanded 
                      ? "68px"  // Tablet/Desktop collapsed
                      : "260px", // Tablet/Desktop expanded
              transition: "margin-left 0.25s ease",
              height: "100vh",          // ✅ lock viewport
              overflow: "hidden",
              // height: isintelligencePage
              //   ? {
              //     xs: "calc(100vh - 0px) !important",
              //     sm: "calc(100vh - 0px) !important",
              //     md: "100vh !important",
              //   } : "100vh !important",
              pb: isintelligencePage ? "0px" : "0px",
              // overflow: "hidden !important",
            }}
          >
            <Routes>
              {getRoutes(routes)}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/" element={<Navigate to="/dashboards/my-dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboards/my-dashboard" replace />} />
            </Routes>
          </MDBox>

        </ThemeProvider>
      )}
    </>
    </FilterProvider>
  );
}
